export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Padrões de coordenada que aparecem no próprio texto de uma URL de mapa, em
 * ordem de precisão:
 *
 * 1. `!3d<lat>!4d<lng>` — coordenada do **pin**, o estabelecimento em si
 * 2. `?q=<lat>,<lng>` / `&q=`
 * 3. `ll=<lat>,<lng>`
 * 4. `@<lat>,<lng>` — centro do **viewport**, onde a câmera do mapa estava
 *
 * Os dois primeiros e o último podem coexistir na mesma URL apontando para
 * lugares diferentes (o `@` costuma cair dezenas ou centenas de metros fora do
 * endereço real), por isso a ordem importa.
 */
const COORDINATE_PATTERNS: RegExp[] = [
  /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
  /[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
  /[?&]ll=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
  /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
];

function isValidLatitude(value: number): boolean {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

function isValidLongitude(value: number): boolean {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

/**
 * Lê a coordenada de um link de mapa **sem fazer requisição**. Links
 * encurtados (`maps.app.goo.gl`, `goo.gl/maps`) não carregam coordenada no
 * texto e retornam `null` — nesse caso a geolocalização é omitida.
 */
export function parseCoordsFromMapLink(
  mapLink: string | null | undefined,
): Coordinates | null {
  if (!mapLink) return null;

  const decoded = safeDecode(mapLink);

  for (const pattern of COORDINATE_PATTERNS) {
    const match = decoded.match(pattern);
    if (!match) continue;

    const latitude = Number(match[1]);
    const longitude = Number(match[2]);

    if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) continue;

    return { latitude, longitude };
  }

  return null;
}

/**
 * Resolve a coordenada da unidade: o valor informado manualmente tem
 * prioridade; sem ele, tenta extrair do link do mapa.
 */
export function resolveCoordinates({
  latitude,
  longitude,
  mapLink,
}: {
  latitude?: number | null;
  longitude?: number | null;
  mapLink?: string | null;
}): Coordinates | null {
  if (
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    isValidLatitude(latitude) &&
    isValidLongitude(longitude)
  ) {
    return { latitude, longitude };
  }

  return parseCoordsFromMapLink(mapLink);
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
